import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../api/client";

/**
 * ADR-009 R6+R7 — Росиинка (API Navigator) в пространстве тенанта.
 *
 * Admin-компонент: активирует продукт для тенанта (POST провижинит в Росиинке),
 * показывает РАЗОВЫЙ ключ (один раз, copy), баланс/usage и интерактивные
 * инструкции (curl + Python OpenAI SDK).
 *
 * Endpoints (admin, auto-JWT через apiClient-интерцептор для /api/admin/*):
 *   GET  /api/admin/tenants/{slug}/products/rosiinka          — статус + баланс
 *   POST /api/admin/tenants/{slug}/products/rosiinka/activate — провижининг (разовый ключ)
 */

// Публичный base-URL Росиинки для сниппетов (совпадает с дефолтом backend-конфига).
const NAV_BASE = "https://api.it-navigator.online";

type RosiinkaStatus = {
  product: string;
  status: string;
  apinav_tenant_id: string;
  key_prefix: string | null;
  activated_at: string | null;
  comp_rub_granted: number | null;
  balance: { available_credits: number; reserved_credits: number } | null;
  usage: { total_charged: number; requests: number } | null;
  balance_available: boolean;
};

type ActivateResult = {
  product: string;
  status: string;
  already_active: boolean;
  apinav_tenant_id: string;
  key_prefix: string | null;
  api_key: string | null;
  credits_granted: number | null;
  comp_rub: number | null;
};

export function RosiinkaPanel({
  tenantSlug,
  tenantName,
}: {
  tenantSlug: string;
  tenantName?: string;
}): ReactElement {
  const queryClient = useQueryClient();
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const statusQuery = useQuery<RosiinkaStatus | null>({
    queryKey: ["rosiinka-status", tenantSlug],
    queryFn: async () => {
      try {
        const r = await apiClient.get(
          `/api/admin/tenants/${tenantSlug}/products/rosiinka`,
        );
        return r.data as RosiinkaStatus;
      } catch (e: any) {
        if (e?.response?.status === 404) return null; // не активирована
        throw e;
      }
    },
    enabled: !!tenantSlug,
  });

  const activateMutation = useMutation({
    mutationFn: async () => {
      const r = await apiClient.post(
        `/api/admin/tenants/${tenantSlug}/products/rosiinka/activate`,
        {},
      );
      return r.data as ActivateResult;
    },
    onSuccess: (data) => {
      setError(null);
      if (data.api_key) setRevealedKey(data.api_key);
      queryClient.invalidateQueries({ queryKey: ["rosiinka-status", tenantSlug] });
    },
    onError: (err: any) => {
      setError(
        err?.response?.data?.detail || err.message || "Ошибка активации",
      );
    },
  });

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  };

  const keyForSnippets = revealedKey ?? "<ВАШ_API_КЛЮЧ>";
  const status = statusQuery.data;
  const isActivated = !!status && status.status === "active";

  return (
    <div style={panelStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <strong style={{ fontSize: "16px" }}>🤖 Росиинка (API Navigator)</strong>
        {isActivated ? (
          <span style={{ ...pill, background: "#e6f7ee", color: "#0a7a4a" }}>активна</span>
        ) : (
          <span style={{ ...pill, background: "#eee", color: "#666" }}>не активирована</span>
        )}
        <span style={{ color: "#888", fontSize: "13px" }}>
          {tenantName ? `${tenantName} · ` : ""}
          <code>{tenantSlug}</code>
        </span>
      </div>

      {statusQuery.isLoading && <p style={{ color: "#666" }}>Загрузка статуса…</p>}

      {error && (
        <div style={{ ...box, background: "#fde8eb", color: "#a01c2a" }}>❌ {error}</div>
      )}

      {/* Не активирована → кнопка активации */}
      {!statusQuery.isLoading && !isActivated && (
        <div style={{ marginTop: "12px" }}>
          <p style={{ color: "#666", marginBottom: "10px" }}>
            Активация создаст клиенту рабочий доступ к Росиинке: API-ключ + стартовый
            comp-баланс. Ключ показывается <strong>один раз</strong>.
          </p>
          <button
            onClick={() => activateMutation.mutate()}
            disabled={activateMutation.isPending}
            style={btnPrimary}
          >
            {activateMutation.isPending ? "Активация…" : "✓ Активировать Росиинку"}
          </button>
        </div>
      )}

      {/* Разовый ключ (после свежей активации) */}
      {revealedKey && (
        <div style={{ ...box, background: "#fff7d6", border: "1px solid #f0c000", marginTop: "12px" }}>
          <strong>🔑 API-ключ клиента (показывается один раз — сохраните!)</strong>
          <div style={{ display: "flex", gap: "8px", marginTop: "8px", alignItems: "center" }}>
            <code style={keyCode}>{revealedKey}</code>
            <button onClick={() => copy(revealedKey, "key")} style={btnSmall}>
              {copied === "key" ? "✓ скопировано" : "Копировать"}
            </button>
          </div>
          <p style={{ color: "#8a6d00", fontSize: "13px", marginTop: "8px" }}>
            Портал ключ не хранит. Передайте его клиенту безопасно. Повторно получить
            нельзя — клиент может выпустить новый через <code>/v2/api-keys</code>.
          </p>
        </div>
      )}

      {/* Статус + баланс/usage */}
      {isActivated && status && (
        <div style={{ marginTop: "12px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px" }}>
          <Metric label="Префикс ключа" value={status.key_prefix ?? "—"} mono />
          <Metric
            label="Баланс (credits)"
            value={
              status.balance_available && status.balance
                ? String(status.balance.available_credits)
                : "недоступно"
            }
          />
          <Metric
            label="Списано / запросов"
            value={
              status.balance_available && status.usage
                ? `${status.usage.total_charged} / ${status.usage.requests}`
                : "—"
            }
          />
          <Metric
            label="Comp-грант (₽)"
            value={status.comp_rub_granted != null ? String(status.comp_rub_granted) : "—"}
          />
        </div>
      )}

      {/* Интерактивные инструкции (R7) — показываем когда активна или только что выдан ключ */}
      {(isActivated || revealedKey) && (
        <Instructions apiKey={keyForSnippets} onCopy={copy} copied={copied} />
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): ReactElement {
  return (
    <div style={{ padding: "10px 12px", background: "#fff", border: "1px solid #eee", borderRadius: "6px" }}>
      <div style={{ color: "#888", fontSize: "12px" }}>{label}</div>
      <div style={{ fontSize: "15px", fontWeight: 600, fontFamily: mono ? "monospace" : "inherit" }}>
        {value}
      </div>
    </div>
  );
}

function Instructions({
  apiKey,
  onCopy,
  copied,
}: {
  apiKey: string;
  onCopy: (text: string, label: string) => void;
  copied: string | null;
}): ReactElement {
  const curlSnippet = `curl -X POST ${NAV_BASE}/v1/images/generations \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"gpt-image-1","prompt":"рыжая лиса в лесу","n":1,"size":"1024x1024"}'`;

  const pySnippet = `from openai import OpenAI

client = OpenAI(api_key="${apiKey}", base_url="${NAV_BASE}/v1")
img = client.images.generate(
    model="gpt-image-1", prompt="рыжая лиса в лесу", n=1, size="1024x1024",
)
print(img.data[0].b64_json[:48], "...")`;

  const balanceSnippet = `curl ${NAV_BASE}/v2/balance -H "Authorization: Bearer ${apiKey}"`;

  return (
    <div style={{ marginTop: "16px" }}>
      <strong style={{ fontSize: "15px" }}>📘 Как работать с продуктом</strong>
      <p style={{ color: "#666", fontSize: "13px", marginTop: "4px" }}>
        OpenAI-совместимый интерфейс. Подставьте ключ клиента вместо{" "}
        <code>&lt;ВАШ_API_КЛЮЧ&gt;</code>.
      </p>

      <Snippet title="Генерация изображения (curl)" code={curlSnippet} label="curl" onCopy={onCopy} copied={copied} />
      <Snippet title="Python (OpenAI SDK)" code={pySnippet} label="py" onCopy={onCopy} copied={copied} />
      <Snippet title="Проверить баланс" code={balanceSnippet} label="bal" onCopy={onCopy} copied={copied} />
    </div>
  );
}

function Snippet({
  title,
  code,
  label,
  onCopy,
  copied,
}: {
  title: string;
  code: string;
  label: string;
  onCopy: (text: string, label: string) => void;
  copied: string | null;
}): ReactElement {
  return (
    <div style={{ marginTop: "10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
        <span style={{ fontSize: "13px", color: "#444", fontWeight: 600 }}>{title}</span>
        <button onClick={() => onCopy(code, label)} style={btnSmall}>
          {copied === label ? "✓ скопировано" : "Копировать"}
        </button>
      </div>
      <pre style={preStyle}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────
const panelStyle: React.CSSProperties = {
  padding: "18px",
  border: "1px solid #ddd",
  borderRadius: "8px",
  background: "#fafbfc",
  fontFamily: "system-ui",
};

const pill: React.CSSProperties = {
  padding: "2px 10px",
  borderRadius: "12px",
  fontSize: "12px",
  fontWeight: 600,
};

const box: React.CSSProperties = {
  marginTop: "8px",
  padding: "12px",
  borderRadius: "6px",
};

const keyCode: React.CSSProperties = {
  background: "#fff",
  padding: "6px 10px",
  borderRadius: "4px",
  fontSize: "13px",
  border: "1px solid #e0c870",
  wordBreak: "break-all",
};

const preStyle: React.CSSProperties = {
  background: "#0f172a",
  color: "#e2e8f0",
  padding: "12px",
  borderRadius: "6px",
  fontSize: "12.5px",
  overflowX: "auto",
  margin: 0,
};

const btnPrimary: React.CSSProperties = {
  padding: "10px 20px",
  fontSize: "14px",
  borderRadius: "4px",
  cursor: "pointer",
  border: "none",
  background: "#0b5fff",
  color: "#fff",
};

const btnSmall: React.CSSProperties = {
  padding: "4px 10px",
  fontSize: "12px",
  border: "1px solid #ccc",
  borderRadius: "3px",
  background: "#fff",
  cursor: "pointer",
};
